import { useState } from "react";
import { useParams } from "react-router-dom";
import { SyncOptions } from "admin/components/dataset-detail/types/dataset-diff";
import useToast from "~/components/ui/toast";
import { apiClient } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const useSyncDataset = () => {
  const { datasetId } = useParams();
  const [isSyncing, setIsSyncing] = useState(false);
  const { showSuccess, showError } = useToast();

  const syncDataset = async (options: SyncOptions): Promise<boolean> => {
    if (!datasetId) {
      showError("Dataset ID is required");
      return false;
    }

    setIsSyncing(true);

    try {
      const queryParams = new URLSearchParams();

      options.sync_component_types.forEach((type) => {
        queryParams.append("sync_component_types", type);
      });

      if (options.sync_access_levels) {
        queryParams.append("sync_access_levels", "true");
      }

      await apiClient(`${VITE_CATALOG_API}/datasets/${datasetId}/sync?${queryParams.toString()}`, {
        method: "POST",
      });

      showSuccess("Dataset synced successfully");

      trackEvent("dataset_admin_sync_completed", {
        dataset_id: datasetId,
        sync_component_types: options.sync_component_types,
        sync_access_levels: options.sync_access_levels,
      });

      return true;
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to sync dataset");

      trackEvent("dataset_admin_sync_failed", {
        dataset_id: datasetId,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  return { syncDataset, isSyncing };
};

export default useSyncDataset;
